import { useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Stack, Typography } from '@mui/material';

import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { FormTextField } from 'components/common/form/Form';
import { AddressField } from 'components/common/form/AddressField';
import { RecipientField } from 'components/shared/sharing/RecipientField';
import { PeopleWithAccess } from 'components/shared/sharing/PeopleWithAccess';
import { EntityFileDetails } from 'components/shared/EntityFileDetails';
import { IllustrationProperties } from 'components/shared/illustrations/IllustrationProperties';

import { editPropertySchema } from '../../validation/form/property';
import { usePropertyDrawers } from 'hooks/usePropertyDrawers';
import { useProperty } from 'hooks/useProperty';
import { useShareProperty } from 'hooks/useShareProperty';
import { listStates } from 'store/slices/properties';

export const EditProperty = (props) => {
  const { close } = props;
  const dispatch = useDispatch();

  const { openDrawerPropertyDetails } = usePropertyDrawers();
  const { handleUpdateProperty } = useProperty();

  const loading = useSelector((state) => state.properties.editPropertyForm.loading);
  const selectedProperty = useSelector((state) => state.properties.selectedProperty);
  const currentUser = useSelector((state) => state.auth.currentUser);

  const { userList, usersLoading, sharedUserList, handleSearchUser, handleShareProperty, handleUpdateShareProperty } =
    useShareProperty(selectedProperty?.id, currentUser);

  const defaultValues = useMemo(() => {
    return {
      address: {
        address: '',
        suburb: '',
        state: '',
        postcode: '',
        place_id: '',
      },
      sales_method: '',
      organisation_id: '',
      contacts: [],
      status: '',
      property_type: '',
      bedrooms: '',
      bathrooms: '',
      carspaces: '',
      vendor_contact: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
      },
      user_name: '',
    };
  }, []);

  const methods = useForm({
    resolver: yupResolver(editPropertySchema),
    defaultValues,
  });

  const handleSave = methods.handleSubmit((data) => {
    const property = {
      address: data?.address,
      sales_method: data?.sales_method,
      bedrooms: data?.bedrooms,
      bathrooms: data?.bathrooms,
      carspaces: data?.carspaces,
      vendor_contact: {
        first_name: data?.vendor_contact?.first_name,
        last_name: data?.vendor_contact?.last_name,
        email: data?.vendor_contact?.email,
        phone: data?.vendor_contact?.phone,
      },
      organisation_id: data?.organisation_id,
      status: data?.status,
      property_type: data?.property_type,
    };
    handleUpdateProperty(data.id, property, data.origin, close);
  });

  const handleCancel = () => {
    openDrawerPropertyDetails();
  };

  const handleFetchPropertyEnums = useCallback(async () => {
    return await Promise.all([
      dispatch(listStates()),
      // dispatch(listStatuses()),
      // dispatch(listTypes()),
      // dispatch(listSalesMethods()),
      // dispatch(listOrganizations()),
    ]);
  }, [dispatch]);

  useEffect(() => {
    handleFetchPropertyEnums();
  }, [handleFetchPropertyEnums]);

  useEffect(() => {
    if (selectedProperty) {
      methods.reset({ ...selectedProperty });
    }
  }, [methods, selectedProperty]);

  const isShared = selectedProperty?.user_id && currentUser?.id && selectedProperty?.user_id !== currentUser?.id;

  return (
    <DrawerContent>
      {!selectedProperty && (
        <DrawerContent.Wrapper>
          <DrawerContent.Header title={selectedProperty?.name} />

          <EntityFileDetails.EmptyFile
            illustration={
              <IllustrationProperties
                width={220}
                height={180}
              />
            }
            title="Select a property to view details"
          />
        </DrawerContent.Wrapper>
      )}
      {selectedProperty && (
        <>
          <DrawerContent.Header title={`Rename ${selectedProperty?.name}`} />
          <DrawerContent.Wrapper>
            <DrawerContent.Content>
              <FormProvider {...methods}>
                <AddressField
                  currentAddress={{ value: methods.watch('address') }}
                  callbacks={{
                    toggleSetValue: (updatedAddress) => {
                      methods.setValue(
                        'address',
                        { ...updatedAddress, place_id: updatedAddress?.place_id ?? '' },
                        { shouldValidate: true }
                      );
                    },
                  }}
                />
                <Typography variant="subtitle2">Extra Details</Typography>
                <FormTextField
                  name="bedrooms"
                  label="Bed"
                  control={methods.control}
                  type="number"
                />
                <FormTextField
                  name="bathrooms"
                  label="Bath"
                  control={methods.control}
                  type="number"
                />
                <FormTextField
                  name="carspaces"
                  label="Car"
                  control={methods.control}
                  type="number"
                />
                <FormTextField
                  name="vendor_contact.first_name"
                  label="Vendor First Name"
                  control={methods.control}
                />
                <FormTextField
                  name="vendor_contact.last_name"
                  label="Vendor Last Name"
                  control={methods.control}
                />
                <FormTextField
                  name="vendor_contact.phone"
                  label="Vendor Phone Number"
                  control={methods.control}
                />
                <FormTextField
                  name="vendor_contact.email"
                  label="Vendor Email"
                  control={methods.control}
                />
                {/* <FormSelectField
                  name="status"
                  label="Status"
                  control={methods.control}
                  options={statuses}
                  valueName="value"
                  optionName="name"
                />
                <FormSelectField
                  name="organisation_id"
                  label="Organisation"
                  control={methods.control}
                  options={organizations}
                  valueName="id"
                  optionName="name"
                />
                <FormSelectField
                  name="sales_method"
                  label="Sales Method"
                  control={methods.control}
                  options={salesMethods}
                  valueName="value"
                  optionName="name"
                />
                <FormSelectField
                  name="property_type"
                  label="Type"
                  control={methods.control}
                  options={types}
                  valueName="value"
                  optionName="name"
                /> */}
                {!isShared && (
                  <Stack
                    pt={1.5}
                    spacing={2}
                  >
                    <Typography variant="subtitle2">Share</Typography>
                    <RecipientField
                      optionsData={userList}
                      backendSearch={handleSearchUser}
                      loading={usersLoading}
                      handleShare={handleShareProperty}
                    />
                    <PeopleWithAccess>
                      <PeopleWithAccess.Account>
                        <PeopleWithAccess.AccountName accountName={selectedProperty?.user_name} />
                        <PeopleWithAccess.AccountAccess accountAccess="Owner" />
                      </PeopleWithAccess.Account>
                      {sharedUserList.map((user, index) => (
                        <PeopleWithAccess.Account key={`${user.owner_account.id}-${index}`}>
                          <PeopleWithAccess.AccountName accountName={user.owner_account.name} />
                          <PeopleWithAccess.AccessOptions
                            value={user.access.includes('Write') ? 'Write' : 'Read'}
                            onChange={(access) => handleUpdateShareProperty(user.owner_account.id, access)}
                          />
                        </PeopleWithAccess.Account>
                      ))}
                    </PeopleWithAccess>
                  </Stack>
                )}
              </FormProvider>
            </DrawerContent.Content>
            <DrawerContent.ActionButtons
              submitButtonText="Save"
              submitButtonLoading={loading}
              submit={handleSave}
              close={handleCancel}
            />
          </DrawerContent.Wrapper>
        </>
      )}
    </DrawerContent>
  );
};
